import { BrowserModule } from '@angular/platform-browser';
import { NgModule, APP_INITIALIZER, Injector, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { AppRoutingModule } from './app-routing.module';
import { ContactsListComponent } from './components/contacts-list/contacts-list.component';
import { MessageViewComponent } from './components/message-view/message-view.component';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { ChatMailErrorHandlingService } from './services/chatmail-error-handling/chatmail-error-handling.service';
import { ChatMailModule } from '@cordic/chatmail-web';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LOCATION_INITIALIZED } from '@angular/common';
import { ChatComponent } from './components/chat/chat.component';
import { AppComponent } from './app.component';
import { UserService } from './services/user-service/user.service';
import { RequestOptionsProviderService } from './services/request-options-provider/request-options-provider.service';
import { FormsModule } from '@angular/forms';
import { ChatBroadcaster } from './services/chat-broadcaster/chat-broadcaster';
import { FriendlyDatePipe } from './pipes/friendly-date.pipe';
import { AlertPopupComponent } from './components/alert-popup/alert-popup.component';
import { CustomPopupComponent } from './components/custom-popup/custom-popup.component';
import { CalendarDayPipe } from './pipes/calendar-day.pipe';
import { AppConfigService } from './services/app-config/app-config.service';


export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json?' + (new Date().getTime()));
}

export function appInitializerFactory(translate: TranslateService, injector: Injector)
{
  return new Promise<any>((resolve: any) =>
  {
    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    locationInitialized.then(() =>
    {
      let langToSet = translate.getBrowserLang();
      if ((langToSet != 'en') &&
          (langToSet != 'fr') &&
          (langToSet != 'de'))
      {
        langToSet = 'en';
      }

      // for now force it to be english while the french/german strings aren't translated...
      // todo: remove after strings have been translated.
      langToSet = 'en';

      translate.setDefaultLang(langToSet);
      translate.use(langToSet).subscribe(() =>
      {
        console.log(`Successfully initialized '${langToSet}' language.'`);
      },
      err =>
      {
        console.error(`Problem with '${langToSet}' language initialization.'`);
      },
      () =>
      {
        resolve(null);
      });
    });
  });
}

const appInitializerFn = (appConfig: AppConfigService, translate: TranslateService, injector: Injector) =>
{
  return (): Promise<any> =>
  {
    return appConfig.load()
    .then(() =>
    {
      return appInitializerFactory(translate, injector);
    });
  };
};

@NgModule({
  declarations: [
    AppComponent,
    ChatComponent,
    ContactsListComponent,
    MessageViewComponent,
    AlertPopupComponent,
    CustomPopupComponent,
    FriendlyDatePipe,
    CalendarDayPipe
  ],
  imports: [
    FormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    ChatMailModule.forRoot([HttpClient,
      RequestOptionsProviderService,
      ChatMailErrorHandlingService]),
  ],
  providers: [ChatBroadcaster,
              UserService,
              {
                provide: APP_INITIALIZER,
                useFactory: appInitializerFn,
                multi: true,
                deps: [AppConfigService, TranslateService, Injector]
              }
            ],
  bootstrap: [AppComponent],
  schemas:   [CUSTOM_ELEMENTS_SCHEMA]

})
export class AppModule { }
